const DRAWER_MEDIA_QUERY = '(max-width: 1199px)';

/**
 * Article TOC sidebar: builds table of contents via tocbot (native scroll)
 * and exposes a mobile drawer for the left sidebar.
 */
class ArticleTocSidebar extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this._depsTimer = null;
    this._resizeObserver = null;
    this._drawerMq = null;
    this._onDrawerMqChange = null;
    this._onTocIconClick = null;
    this._onOverlayClick = null;
    this._onDocumentClick = null;
    this._onKeydown = null;
    this._onTocLinkClick = null;
  }

  connectedCallback() {
    this.waitForDependencies();
  }

  disconnectedCallback() {
    this.destroy();
  }

  waitForDependencies() {
    const check = () => {
      if (window.tocbot) {
        requestAnimationFrame(() => this.init());
        return;
      }
      this._depsTimer = setTimeout(check, 100);
    };
    check();
  }

  destroy() {
    clearTimeout(this._depsTimer);
    this._depsTimer = null;

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    if (this._drawerMq && this._onDrawerMqChange) {
      this._drawerMq.removeEventListener('change', this._onDrawerMqChange);
    }

    if (this._tocIcon && this._onTocIconClick) {
      this._tocIcon.removeEventListener('click', this._onTocIconClick);
    }

    if (this._overlay && this._onOverlayClick) {
      this._overlay.removeEventListener('click', this._onOverlayClick);
    }

    if (this._onDocumentClick) {
      document.removeEventListener('click', this._onDocumentClick);
    }

    if (this._onKeydown) {
      document.removeEventListener('keydown', this._onKeydown);
    }

    if (this.tocEl && this._onTocLinkClick) {
      this.tocEl.removeEventListener('click', this._onTocLinkClick);
    }

    if (window.tocbot && this._initialized) {
      window.tocbot.destroy();
    }

    this.closeDrawer();
    this._initialized = false;
  }

  init() {
    if (this._initialized) return;

    this.sidebar = this.querySelector('.article-sidebar--left');
    this.sidebarInner = this.querySelector('.article-sidebar__inner');
    this.tocEl = this.querySelector('.js-toc');
    this.tocIcon = this.querySelector('.toc-icon');
    this.overlay = this.querySelector('.toc-overlay');
    this.contentEl = this.querySelector('.js-toc-content');
    this.tocWidget = this.querySelector('[data-toc-widget]');

    if (!this.sidebar || !this.contentEl) return;

    const headingCount = this.assignHeadingIds();

    if (this.tocEl && headingCount > 0) {
      this.initTocbot();
      this.bindTocLinkClick();
      this.bindResizeObserver();
    } else if (this.tocWidget) {
      this.tocWidget.hidden = true;
    }

    this.setupDrawer();
    this._initialized = true;
  }

  assignHeadingIds() {
    const selector = this.tocEl?.dataset.headingSelector || 'h2, h3, h4';
    const headings = this.contentEl.querySelectorAll(selector);
    const usedIds = new Set();

    headings.forEach((heading, index) => {
      if (heading.id) {
        usedIds.add(heading.id);
        return;
      }

      const slug = (heading.textContent || '')
        .trim()
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .slice(0, 80);

      let id = slug || `section-${index + 1}`;
      let suffix = 1;

      while (usedIds.has(id) || document.getElementById(id)) {
        id = `${slug || 'section'}-${suffix}`;
        suffix += 1;
      }

      heading.id = id;
      usedIds.add(id);
    });

    return headings.length;
  }

  getHeaderOffset() {
    const header = document.querySelector('header');
    return (header?.offsetHeight || 0) + 200;
  }

  initTocbot() {
    const headingSelector = this.tocEl.dataset.headingSelector || 'h2, h3, h4';
    const headerOffset = this.getHeaderOffset();

    window.tocbot.init({
      tocSelector: `#${this.tocEl.id}`,
      contentSelector: '.js-toc-content',
      headingSelector,
      scrollSmooth: true,
      scrollSmoothDuration: 400,
      scrollSmoothOffset: -headerOffset,
      headingsOffset: headerOffset,
      hasInnerContainers: true,
      enableUrlHashUpdateOnScroll: false,
      scrollHandlerTimeout: 32,
    });
  }

  bindTocLinkClick() {
    this._onTocLinkClick = (event) => {
      if (!event.target.closest('a.toc-link')) return;
      if (this.isDrawerMode() && this.sidebar?.classList.contains('is-open')) {
        this.closeDrawer();
      }
    };
    this.tocEl.addEventListener('click', this._onTocLinkClick);
  }

  bindResizeObserver() {
    if (!window.ResizeObserver || !this.contentEl) return;

    let resizeRaf = null;
    this._resizeObserver = new ResizeObserver(() => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        if (window.tocbot) window.tocbot.refresh();
      });
    });

    this._resizeObserver.observe(this.contentEl);
  }

  isDrawerMode() {
    return window.matchMedia(DRAWER_MEDIA_QUERY).matches;
  }

  setupDrawer() {
    this._drawerMq = window.matchMedia(DRAWER_MEDIA_QUERY);
    this._onDrawerMqChange = () => this.updateDrawerUi();
    this._drawerMq.addEventListener('change', this._onDrawerMqChange);

    if (this.tocIcon) {
      this._onTocIconClick = (event) => {
        event.preventDefault();
        this.toggleDrawer();
      };
      this.tocIcon.addEventListener('click', this._onTocIconClick);
    }

    if (this.overlay) {
      this._onOverlayClick = () => this.closeDrawer();
      this.overlay.addEventListener('click', this._onOverlayClick);
    }

    this._onDocumentClick = (event) => {
      if (!this.isDrawerMode() || !this.sidebar?.classList.contains('is-open')) return;
      if (event.target.closest('.article-sidebar--left') === this.sidebar) return;
      if (event.target.closest('.toc-icon') === this.tocIcon) return;
      this.closeDrawer();
    };
    document.addEventListener('click', this._onDocumentClick);

    this._onKeydown = (event) => {
      if (event.code === 'Escape') this.closeDrawer();
    };
    document.addEventListener('keydown', this._onKeydown);

    this.updateDrawerUi();
  }

  updateDrawerUi() {
    if (!this.tocIcon) return;

    const hasSidebarContent = Boolean(this.sidebarInner?.querySelector('.widget, .share__post, .article-tags'));
    const showFab = hasSidebarContent && this.isDrawerMode();

    this.tocIcon.hidden = !showFab;

    if (!hasSidebarContent) {
      this.closeDrawer();
      return;
    }

    if (!this.isDrawerMode()) {
      this.closeDrawer();
    }
  }

  toggleDrawer() {
    if (this.sidebar?.classList.contains('is-open')) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  openDrawer() {
    if (!this.sidebar || !this.isDrawerMode()) return;

    this.sidebar.classList.add('is-open');
    this.overlay?.classList.add('is-visible');
    this.overlay?.removeAttribute('hidden');
    this.tocIcon?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    if (this.sidebarInner) {
      window.FoxTheme?.a11y?.trapFocus(this.sidebarInner);
    }
  }

  closeDrawer() {
    if (!this.sidebar) return;

    this.sidebar.classList.remove('is-open');
    this.overlay?.classList.remove('is-visible');
    this.overlay?.setAttribute('hidden', '');
    this.tocIcon?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    if (this.sidebarInner) {
      window.FoxTheme?.a11y?.removeTrapFocus(this.sidebarInner);
    }
  }
}

if (!customElements.get('article-toc-sidebar')) {
  customElements.define('article-toc-sidebar', ArticleTocSidebar);
}
